import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import colors from '../../utils/colors';

interface ICustomSelectGroupProps {
	label: string;
	options: JSX.Element[];
    isOpen: boolean;
    changeIsOpen: Function;
    closedOption?: JSX.Element;
	invalid?: boolean;
	labelStyle?: React.CSSProperties;
	optionIndex?: number;
    hideClosedOption?: boolean;
}

/* 
----
Note: if sufficiently flexible, this could potentially replace / be used by: 
* `select-group.tsx` 
* `select-small.tsx` 
----
*/
export default function CustomSelectGroup({
	label,
	options,
	isOpen,
    changeIsOpen,
    closedOption,
	invalid,
	labelStyle,
	optionIndex = 0,
    hideClosedOption = false
}: ICustomSelectGroupProps): JSX.Element {
	const [activeClass, setActiveClass] = useState('closed');

	function animationFinished() {
		if (isOpen && activeClass === 'closed') {
			changeIsOpen(false);
		}
	}

	function close() {
        changeIsOpen(false);
		setActiveClass('closed');
	}

	function _open() {
		changeIsOpen(true);
		setTimeout(() => setActiveClass('open'), 16);
	}

    const selectedOption = useMemo((): JSX.Element => {
        return options[optionIndex] ?? <></>;
    }, [optionIndex, options]);

    const customClass = useMemo(() => {
        return label.split(' ').join('-').toLowerCase();
    }, [label]);

	return (
		<div
			className={classNames('custom-select-group', {
				open: isOpen,
				invalid: invalid || false,
			})}
		>
			<label
				className="select-label"
				onClick={_open}
				style={labelStyle ? labelStyle : {}}
			>
				{label}
			</label>
			<div className="select-background" onClick={close}></div>
			<span
				onClick={_open}
				className={classNames('main-label', {
                    'should-hide': hideClosedOption,
					open: activeClass !== 'closed',
				})}
			>
				{closedOption ?? selectedOption}
			</span>
			<div
				onClick={!isOpen ? _open : close}
				className={classNames('select-body', customClass, activeClass)}
				onTransitionEnd={animationFinished}
			>
                <div className="select-body__inner overflow-scroll-y">
				    {options}
                </div>
			</div>
            <span className="caret" onClick={!isOpen ? _open : close}>
                <svg
                    width="11"
                    height="6"
                    viewBox="0 0 11 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1.27094 0.228824C0.9901 -0.0650263 0.523473 -0.0762935 0.228695 0.203658C-0.0660828 0.48361 -0.0773854 0.948767 0.20345 1.24262C0.20345 1.24262 4.60439 5.77719 4.61717 5.78955C4.92128 6.07934 5.40348 6.06851 5.69419 5.76535L10.0814 1.24266C10.3627 0.949277 10.3522 0.484101 10.0579 0.203658C9.7636 -0.0767845 9.29696 -0.0662955 9.01563 0.227086L5.34256 3.94922C5.23294 4.06031 5.05365 4.06033 4.944 3.94927L1.27094 0.228824Z"
                        fill={colors.textGray}
                    />
                </svg>
            </span>
		</div>
	);
}
